// @flow
import type {
  ControlsInfo,
  Crop,
  OutputCrop,
  ProviderTemplateFieldsInfo,
  ValidatedAdvertisementWithStoredMedia
} from './types'
import store from '../redux/store'
import type { CommonFields, MediaState } from '../redux/ducks/advertisementTemplate/reducers'
import type { Store } from '../redux/ducks'
import { getCarousel, getMedia } from '../redux/selectors'
import _ from 'lodash'
import {
  defaultFbLinkButton,
  defaultTmLinkButton,
  defaultVkLinkButton,
  Audience,
  ProviderTemplateNames as TPL
} from './constants'
import { mapControlsDataToProviderTemplatesData } from './connectProviderTemplatesWithControls'
import type { Carousel, CarouselCard } from '../redux/ducks/carouselEdit/reducers'
import type { PlacementType } from './AudienceEdit/PlacementTypeSelect'

type MaybeNilNumber = number | void | null

type VkOutputCarousel = {|
  cards: Array<{|
    url: string,
    title: string,
    image_id: MaybeNilNumber,
    image_crop: OutputCrop | null,
    vk_link_button: string,
    price: string,
    old_price: string
  |}>
|}

type TmOutputCarousel = {|
  cards: Array<{|
    url: string,
    title: string,
    description: string,
    image_id: MaybeNilNumber,
    image_crop: OutputCrop | null
  |}>
|}

type FbOutputCarousel = {|
  cards: Array<{|
    url: string,
    title: string,
    description: string,
    image_id: MaybeNilNumber,
    image_crop: OutputCrop | null
  |}>
|}

export type RequestPayloadItem = $Shape<ProviderTemplateFieldsInfo<any>>
export type RequestPayload = Array<RequestPayloadItem>

export function singleProviderOutputItem(providerCrop: ?Crop): OutputCrop | null {
  if (!providerCrop) return null
  return {
    left: providerCrop.x,
    top: providerCrop.y,
    width: providerCrop.width,
    height: providerCrop.height
  }
}

function vkCarouselBlock(state: Store, carousel: Carousel): VkOutputCarousel {
  return {
    cards: _.map(carousel.cards, (card: CarouselCard) => {
      return {
        title: card.title,
        url: card.url,
        image_id: getImageId(state, card.imageStoreId),
        image_crop: singleProviderOutputItem(card.crop),
        vk_link_button: card.vkLinkButton,
        price: card.price,
        old_price: card.oldPrice
      }
    })
  }
}

function tmCarouselBlock(state: Store, carousel: Carousel): TmOutputCarousel {
  return {
    cards: _.map(carousel.cards, (card: CarouselCard) => {
      return {
        title: card.title,
        url: card.url,
        description: card.description,
        image_id: getImageId(state, card.imageStoreId),
        image_crop: singleProviderOutputItem(card.crop)
      }
    })
  }
}

function fbCarouselBlock(state: Store, carousel: Carousel): FbOutputCarousel {
  return {
    cards: _.map(carousel.cards, (card: CarouselCard) => {
      return {
        title: card.title,
        url: card.url,
        description: card.description,
        image_id: getImageId(state, card.imageStoreId),
        image_crop: singleProviderOutputItem(card.crop)
      }
    })
  }
}

export function advertisementsPayload(
  advertisements: Array<ValidatedAdvertisementWithStoredMedia>,
  providers: Array<string>,
  audienceType: string,
  placementType: PlacementType,
  vkPlacement: string
): RequestPayload {
  const providerTemplateNames = adProviderTemplateNames(
    providers,
    audienceType,
    placementType,
    vkPlacement
  )
  return _.map(advertisements, (advertisement) =>
    _.pick(
      (prepareProviderTemplateBlocks(advertisement): RequestPayloadItem),
      providerTemplateNames
    )
  )
}

export function adProviderTemplateNames(
  providers: Array<string>,
  audienceType: string,
  placementType: PlacementType,
  vkPlacement: string
): Array<string> {
  const result: Array<string> = []

  if (_.includes(providers, 'vk')) {
    if (audienceType === Audience.types.posts_promotion) {
      result.push(TPL.vk_promotion)
    } else if (audienceType === Audience.types.leadform) {
      result.push(TPL.vk_leadform)
    } else if (placementType === Audience.placement_types.column) {
      result.push(TPL.vk_column)
    } else if (placementType === Audience.placement_types.carousel) {
      result.push(TPL.vk_carousel)
    } else if (placementType === Audience.placement_types.with_video) {
      result.push(TPL.vk_video)
    } else if (vkPlacement === Audience.vk_placements.promopost_without_button) {
      result.push(TPL.vk_article)
    } else if (vkPlacement === Audience.vk_placements.promopost_without_public) {
      result.push(TPL.vk_site)
    } else if (vkPlacement === Audience.vk_placements.promopost_with_big_image) {
      result.push(TPL.vk_big_image)
    } else {
      result.push(TPL.vk_multi)
    }
  }
  if (_.includes(providers, 'target_mail')) {
    if (audienceType === Audience.types.product_retargeting) {
      result.push(TPL.tm_product)
    } else if (audienceType === Audience.types.posts_promotion) {
      result.push(TPL.tm_promotion)
    } else if (placementType === Audience.placement_types.column) {
      result.push(TPL.tm_column)
    } else if (placementType === Audience.placement_types.with_video) {
      result.push(TPL.tm_video)
    } else if (placementType === Audience.placement_types.carousel) {
      result.push(TPL.tm_carousel)
    } else {
      result.push(TPL.tm_multi)
    }
  }
  if (_.includes(providers, 'facebook')) {
    if (audienceType === Audience.types.product_retargeting) {
      result.push(TPL.fb_product)
    } else if (audienceType === Audience.types.leadform) {
      result.push(TPL.fb_leadform)
    } else if (audienceType === Audience.types.posts_promotion) {
      result.push(TPL.fb_promotion)
    } else if (placementType === Audience.placement_types.with_video) {
      result.push(TPL.fb_video)
    } else if (placementType === Audience.placement_types.carousel) {
      result.push(TPL.fb_carousel)
    } else {
      result.push(TPL.fb_multi)
    }
  }
  if (_.includes(providers, 'google')) {
    if (placementType === Audience.placement_types.with_video) {
      result.push(TPL.gl_video)
    } else {
      result.push(TPL.gl_multi)
    }
  }

  return result
}

function prepareFields(
  ad: ValidatedAdvertisementWithStoredMedia
): {| common: CommonFields, media: MediaState |} {
  const common: CommonFields = {
    id: null,
    name: 'dummy',
    url: ad.url,
    vkUrl: ad.vkUrl || '',
    tmUrl: ad.tmUrl || '',
    fbUrl: ad.fbUrl || '',
    title: ad.title,
    productTitle: ad.productTitle || '',
    vkTitle: ad.vkTitle || '',
    fbTitle: ad.fbTitle || '',
    columnDescription: ad.columnDescription,
    productDescription: ad.productDescription || '',
    multiDescription: ad.multiDescription,
    vkMultiDescription: ad.vkMultiDescription || '',
    vkWithoutButtonDescription: ad.vkWithoutButtonDescription || '',
    vkLinkTitle: ad.vkLinkTitle || '',
    fbMultiDescription: ad.fbMultiDescription || '',
    logoImageStoreId: ad.logoImageStoreId || null,
    logoImageCrop: ad.logoImageCrop || null,
    vkLinkButton: ad.vkLinkButton || null,
    tmLinkButton: ad.tmLinkButton || null,
    fbLinkButton: ad.fbLinkButton || null,
    snippetImageStoreId: ad.snippetImageStoreId || null,
    snippetImageCrop: ad.snippetImageCrop || null,
    carouselStoreId: ad.carouselStoreId || null,
    tmCarouselDescription: ad.tmCarouselDescription || ''
  }

  const media: MediaState = {
    columnImageStoreId: ad.columnImageStoreId || null,
    multiImageStoreId: ad.multiImageStoreId || null,
    tmSmallImageStoreId: ad.tmSmallImageStoreId || null,
    fbInstagramImageStoreId: ad.fbInstagramImageStoreId || null,
    fbStoriesImageStoreId: ad.fbStoriesImageStoreId || null,
    vkColumnImageCrop: ad.vkColumnCrop || null,
    vkMultiImageCrop: ad.vkMultiCrop || null,
    vkArticleImageCrop: ad.vkArticleCrop || null,
    vkSiteImageCrop: ad.vkSiteCrop || null,
    tmColumnImageCrop: ad.tmColumnCrop || null,
    tmMultiImageCrop: ad.tmMultiCrop || null,
    tmSmallImageCrop: ad.tmSmallImageCrop || null,
    fbImageCrop: ad.fbImageCrop || null,
    fbInstagramImageCrop: ad.fbInstagramImageCrop || null,
    fbStoriesImageCrop: ad.fbStoriesImageCrop || null,
    glImageCrop: ad.glImageCrop || null,
    videoStoreId: ad.videoStoreId || null,
    fbThumbnailImageStoreId: ad.fbThumbnailImageStoreId || null,
    fbThumbnailImageCrop: ad.fbThumbnailImageCrop || null,
    vkBigImageStoreId: ad.vkBigImageStoreId || null
  }

  return { common, media }
}
export function prepareProviderTemplateBlocks(
  ad: ValidatedAdvertisementWithStoredMedia
): RequestPayloadItem {
  const { common, media } = prepareFields(ad)

  return prepareTplProviderTemplateBlocks(store.getState(), common, media)
}

function carouselData(
  state: Store,
  carouselStoreId
): {|
  vkCarousel: ?VkOutputCarousel,
  tmCarousel: ?TmOutputCarousel,
  fbCarousel: ?FbOutputCarousel
|} {
  const carousel = carouselStoreId && getCarousel(state, carouselStoreId)

  if (carousel) {
    return {
      vkCarousel: vkCarouselBlock(state, carousel),
      tmCarousel: tmCarouselBlock(state, carousel),
      fbCarousel: fbCarouselBlock(state, carousel)
    }
  } else {
    return {
      vkCarousel: null,
      tmCarousel: null,
      fbCarousel: null
    }
  }
}

export function prepareTplProviderTemplateBlocks(
  state: Store,
  data: CommonFields,
  media: MediaState
): ProviderTemplateFieldsInfo<any> {
  const multiImageId = getImageId(state, media.multiImageStoreId)

  const controlsData: ControlsInfo<any> = {
    url: data.url,
    vkUrl: data.vkUrl,
    tmUrl: data.tmUrl,
    fbUrl: data.fbUrl,
    title: data.title,
    vkTitle: data.vkTitle,
    fbTitle: data.fbTitle,
    productTitle: data.productTitle,
    columnDescription: data.columnDescription,
    multiDescription: data.multiDescription,
    productDescription: data.productDescription,
    vkMultiDescription: data.vkMultiDescription,
    vkWithoutButtonDescription: data.vkWithoutButtonDescription,
    vkLinkTitle: data.vkLinkTitle,
    fbMultiDescription: data.fbMultiDescription,
    tmCarouselDescription: data.tmCarouselDescription,
    columnImage: getImageId(state, media.columnImageStoreId),
    columnImageData: null,
    multiImage: multiImageId,
    multiImageData: null,
    logoImage: getImageId(state, data.logoImageStoreId),
    logoImageData: null,
    tmSmallImage: getImageId(state, media.tmSmallImageStoreId) || multiImageId,
    tmSmallImageData: null,
    fbInstagramImage: getImageId(state, media.fbInstagramImageStoreId),
    fbInstagramImageData: null,
    fbStoriesImage: getImageId(state, media.fbStoriesImageStoreId),
    fbStoriesImageData: null,
    vkBigImage: getImageId(state, media.vkBigImageStoreId),
    vkBigImageData: null,
    vkColumnCrop: singleProviderOutputItem(media.vkColumnImageCrop),
    vkMultiCrop: singleProviderOutputItem(media.vkMultiImageCrop),
    vkArticleCrop: singleProviderOutputItem(media.vkArticleImageCrop),
    vkSiteCrop: singleProviderOutputItem(media.vkSiteImageCrop),
    tmColumnCrop: singleProviderOutputItem(media.tmColumnImageCrop),
    tmMultiCrop: singleProviderOutputItem(media.tmMultiImageCrop),
    fbMultiCrop: singleProviderOutputItem(media.fbImageCrop),
    glMultiCrop: singleProviderOutputItem(media.glImageCrop),
    vkLinkButton: data.vkLinkButton || defaultVkLinkButton,
    tmLinkButton: data.tmLinkButton || defaultTmLinkButton,
    fbLinkButton: data.fbLinkButton || defaultFbLinkButton,
    video: getVideoId(state, media.videoStoreId),
    videoData: null,
    fbThumbnailImage: getImageId(state, media.fbThumbnailImageStoreId),
    fbThumbnailImageData: null,
    snippetImage: getImageId(state, data.snippetImageStoreId),
    snippetImageData: null,
    fbInstagramCrop: singleProviderOutputItem(media.fbInstagramImageCrop),
    fbStoriesCrop: singleProviderOutputItem(media.fbStoriesImageCrop),
    fbThumbnailCrop: singleProviderOutputItem(media.fbThumbnailImageCrop),
    logoCrop: singleProviderOutputItem(data.logoImageCrop),
    tmSmallCrop: singleProviderOutputItem(media.tmSmallImageCrop),
    snippetCrop: singleProviderOutputItem(data.snippetImageCrop),
    carousel: null,
    ...carouselData(state, data.carouselStoreId),
    vk: null,
    tm: null,
    fb: null,
    gl: null
  }

  return mapControlsDataToProviderTemplatesData(controlsData)
}

function getImageId(state: Store, storeImageId: ?string): number | null {
  return storeImageId ? getMedia(state, storeImageId).id : null
}

function getVideoId(state: Store, storeVideoId: ?string): number | null {
  return storeVideoId ? getMedia(state, storeVideoId).id : null
}
